// src/screen/Dashboard.js

import React, { useState, useEffect, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { FaBars, FaTimes, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';

// Local Imports
import { useAuth } from '../context/AuthContext'; // Auth context
import { ChatContext, ChatProvider } from '../context/ChatContext'; // Chat context

// Component Imports
import BottomNav from '../component/BottomNav'; // Will receive counts
import Chat from '../component/Chat';
import Home from '../component/Home'; // Will receive callback
import Cart from '../component/Cart';
import Profile from '../component/Profile';
import SidePanel from '../component/SidePanel';
import UploadDocumentsPopup from '../component/UploadDocumentsPopup';
import ApplicationHistory from '../component/ApplicationHistory';
import AssignApplicantPopup from '../component/AssignApplicantPopup';
import AdditionalFieldsPopup from '../component/AdditionalFieldsPopup';
import apiClient from '../api/apiClient';

// --- Helper Functions (Keep as is) ---
const renderLoadingIndicator = (text = "Loading...", styles = {}) => (
    <div style={{ ...styles.loadingIndicator, padding: '2rem', textAlign: 'center', color: '#555', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <FaSpinner className="fa-spin" style={{ marginRight: '0.5rem' }} /> {text}
    </div>
);
const renderErrorIndicator = (errorText, styles = {}) => (
    <div style={{ ...styles.errorIndicator, padding: '1rem', margin: '1rem', textAlign: 'center', color: '#c62828', border: '1px solid #ef9a9a', borderRadius: '4px', backgroundColor: '#ffebee', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
        <FaExclamationTriangle style={{ marginRight: '0.5rem' }} /> Error: {errorText || 'Unknown error'}
    </div>
);

// Styles for the Toggle Button (Keep as is)
const toggleButtonStyle = (isOpen) => ({
    position: 'fixed',
    top: '15px',
    left: isOpen ? '290px' : '15px',
    zIndex: 1100,
    background: 'rgba(255, 255, 255, 0.9)',
    border: '1px solid #ccc',
    borderRadius: '50%',
    padding: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.2rem',
    color: '#333',
    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
    transition: 'left 0.3s ease-in-out, background-color 0.2s, color 0.2s',
});

// --- Main Dashboard UI Component (Accepts counts and callback, passes them down) ---
const Dashboard = ({
    // Props related to data display
    bookingHistory,
    isLoadingHistory,
    errorHistory,
    historyStyles,
    activeTab,
    setActiveTab,
    isLoadingFieldsDef,
    // Props related to triggering actions (passed down)
    handleTriggerAssignmentFlow,
    // Props related to Chat context (passed down if SidePanel/Chat need them directly)
    chatContextValue,
    // Props for badge counts
    cartItemCount,
    historyActionCount,
    // *** NEW: Accept the trigger function prop ***
    triggerDataRefresh,
}) => {
    const { user, logout } = useAuth();
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const {
        chatState = { sessionsList: [], activeSessionId: null, isLoadingSessions: false, sessionsError: null },
        loadSession = () => console.warn("loadSession called before context ready"),
        fetchSessions = () => console.warn("fetchSessions called before context ready")
    } = chatContextValue || {};

    useEffect(() => {
        localStorage.setItem('activeTab', activeTab);
    }, [activeTab]);

    const togglePanel = () => {
        if (activeTab === 'chat') setIsPanelOpen(prev => !prev);
        else setIsPanelOpen(false);
    };

    useEffect(() => {
        if (activeTab !== 'chat') setIsPanelOpen(false);
    }, [activeTab]);

    const handleSelectSession = (sessionId) => {
        if (chatState && sessionId !== chatState.activeSessionId) {
            loadSession(sessionId);
            setIsPanelOpen(false);
        } else if (!chatState) {
            console.warn("Chat state not available for session selection.");
        }
    };

    const handleLogoutRequest = () => {
        if (window.confirm("Are you sure you want to log out?")) {
            logout();
            toast.info("You have been logged out.");
            setActiveTab('home');
            localStorage.removeItem('activeTab');
        }
    };

    // --- Render Main Content based on Tab (Pass callback to Home) ---
    const renderContent = () => {
        if (isLoadingFieldsDef && !isLoadingHistory) {
            return renderLoadingIndicator("Loading application requirements...", historyStyles);
        }
        switch (activeTab) {
            // *** UPDATED: Pass triggerDataRefresh to Home ***
            case 'home':
                return <Home onCartUpdate={triggerDataRefresh} />; // Pass function as 'onCartUpdate' prop

            case 'history':
                if (isLoadingHistory) return renderLoadingIndicator("Loading application history...");
                if (errorHistory && !isLoadingHistory) return renderErrorIndicator(errorHistory);
                return (
                   <div style={{ padding: '1rem 1.5rem' }}>
                       <ApplicationHistory
                           isLoading={false}
                           error={null}
                           historyData={bookingHistory}
                           onViewBooking={handleTriggerAssignmentFlow}
                           styles={historyStyles}
                       />
                   </div>
                );
            case 'chat':
                return (
                    <>
                        <button onClick={togglePanel} style={toggleButtonStyle(isPanelOpen)} title={isPanelOpen ? "Close Menu" : "Open Menu"} aria-label={isPanelOpen ? "Close session menu" : "Open session menu"}>
                            {isPanelOpen ? <FaTimes /> : <FaBars />}
                        </button>
                        <Chat />
                    </>
                );
            case 'cart': return <Cart />;
            case 'profile': return <Profile />;
            // *** UPDATED: Pass triggerDataRefresh to Home in default case ***
            default:
                 return <Home onCartUpdate={triggerDataRefresh} />; // Pass function as 'onCartUpdate' prop
        }
    };
    // --- End Render Main Content ---

    return (
        <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', position: 'relative' }}>
            {activeTab === 'chat' && (
                <SidePanel
                    isOpen={isPanelOpen}
                    togglePanel={togglePanel}
                    sessions={chatState.sessionsList || []}
                    onSelectSession={handleSelectSession}
                    activeSessionId={chatState.activeSessionId}
                    isLoading={chatState.isLoadingSessions || false}
                    error={chatState.sessionsError}
                    onRefreshSessions={fetchSessions}
                />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%', transition: 'margin-left 0.3s ease-in-out', marginLeft: (activeTab === 'chat' && isPanelOpen) ? '280px' : '0px', overflow: 'hidden', position: 'relative', }}>
                <main style={{ flex: 1, backgroundColor: '#F5F5F5', overflowY: 'auto', position: 'relative', paddingBottom: '70px' }}>
                    {renderContent()}
                </main>
                {/* Pass the counts down to BottomNav */}
                <BottomNav
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    onLogoutClick={handleLogoutRequest}
                    cartItemCount={cartItemCount} // Pass down
                    historyActionCount={historyActionCount} // Pass down
                 />
            </div>
        </div>
    );
};

// --- PropTypes (Added triggerDataRefresh) ---
Dashboard.propTypes = {
  bookingHistory: PropTypes.array.isRequired,
  isLoadingHistory: PropTypes.bool.isRequired,
  errorHistory: PropTypes.string,
  historyStyles: PropTypes.object.isRequired,
  activeTab: PropTypes.string.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  isLoadingFieldsDef: PropTypes.bool,
  handleTriggerAssignmentFlow: PropTypes.func.isRequired,
  chatContextValue: PropTypes.object,
  cartItemCount: PropTypes.number,
  historyActionCount: PropTypes.number,
  // *** NEW ***
  triggerDataRefresh: PropTypes.func.isRequired, // Add propType for the function
};
// --- DefaultProps (No change needed here) ---
Dashboard.defaultProps = {
    isLoadingFieldsDef: false,
    errorHistory: null,
    chatContextValue: null,
    cartItemCount: 0,
    historyActionCount: 0,
    // No default needed for required triggerDataRefresh prop
};

// --- Define actionable statuses (used for badge count) ---
const ACTIONABLE_HISTORY_STATUSES = [
    'paid',
    'pending information',
    'pending applicant info',
    'pending documents',
    'rejected'
].map(s => s.toLowerCase()); // Ensure lowercase for comparison

// --- Wrapper component that PROVIDES context, manages ALL popup states, AND fetches data for badges ---
const DashboardWithChat = () => {
    const { user } = useAuth();

    // --- Popup State (Existing) ---
    const [isUploadPopupOpen, setIsUploadPopupOpen] = useState(false);
    const [selectedOrderItemIdForUpload, setSelectedOrderItemIdForUpload] = useState(null);
    const [isAssignPopupOpen, setIsAssignPopupOpen] = useState(false);
    const [orderItemIdForAssignment, setOrderItemIdForAssignment] = useState(null);
    const [isFieldsPopupOpen, setIsFieldsPopupOpen] = useState(false);
    const [orderItemIdForFields, setOrderItemIdForFields] = useState(null);
    const [fieldsDefinitionForPopup, setFieldsDefinitionForPopup] = useState(null);
    const [isLoadingFieldsDef, setIsLoadingFieldsDef] = useState(false);
    const [fieldsContextData, setFieldsContextData] = useState({
        serviceName: null, applicantName: null, applicantId: null,
    });

    // --- History State (Existing) ---
    const [bookingHistory, setBookingHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [errorHistory, setErrorHistory] = useState(null);
    const [refreshHistoryTrigger, setRefreshHistoryTrigger] = useState(0);
    const [activeTab, setActiveTab] = useState(() => localStorage.getItem('activeTab') || 'home');

    // --- Cart State (Existing) ---
    const [cartItems, setCartItems] = useState([]);
    const [isLoadingCart, setIsLoadingCart] = useState(false);
    const [errorCart, setErrorCart] = useState(null);

    // --- Timestamp State (Existing) ---
    const [dataChangeTimestamp, setDataChangeTimestamp] = useState(Date.now());


    // --- Fetch History Data (Existing) ---
    const fetchHistory = useCallback(async () => {
        if (!user) {
            setBookingHistory([]); setIsLoadingHistory(false); setErrorHistory(null); return;
        }
        setIsLoadingHistory(true); setErrorHistory(null);
        try {
            const response = await apiClient.get('/orders');
            setBookingHistory(response.data || []);
        } catch (err) {
            const errorMsg = err.response?.data?.error || 'Failed to load application history.';
            setErrorHistory(errorMsg); toast.error(errorMsg); setBookingHistory([]);
        } finally { setIsLoadingHistory(false); }
    }, [user]);

    // --- Fetch Cart Data (Existing) ---
    const fetchCart = useCallback(async () => {
        if (!user) {
            setCartItems([]); setIsLoadingCart(false); setErrorCart(null); return;
        }
        setIsLoadingCart(true); setErrorCart(null);
        try {
            const response = await apiClient.get('/cart');
            setCartItems(response.data || []);
        } catch (err) {
            console.error("Dashboard: Error fetching cart for badge:", err);
            const errorMsg = err.response?.data?.error || 'Failed to load cart info.';
            setErrorCart(errorMsg);
            setCartItems([]);
        } finally {
            setIsLoadingCart(false);
        }
    }, [user]);

    // --- *** NEW: Define the stable callback function *** ---
    const triggerDataRefresh = useCallback(() => {
        console.log('[DashboardWithChat] Refresh triggered');
        setDataChangeTimestamp(Date.now());
    }, []); // Empty dependency array makes this function stable and safe to pass down


    // --- Combined Data Fetching Effect (Updated) ---
    useEffect(() => {
        if (user) {
            console.log("[Dashboard] Fetching history and cart data...");
            fetchHistory();
            fetchCart();
        }
    }, [user, dataChangeTimestamp, fetchHistory, fetchCart]); // Add dataChangeTimestamp


    // --- Storage Listener Effect (Kept for cross-tab sync potential) ---
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'cartUpdated') {
                console.log('[Dashboard] Storage update detected (`cartUpdated`), refreshing data via storage event...');
                setDataChangeTimestamp(Date.now());
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);


    // --- Calculate Counts (Existing) ---
    const cartItemCount = cartItems.length;
    const historyActionCount = bookingHistory.filter(item =>
        ACTIONABLE_HISTORY_STATUSES.includes(item.status?.toLowerCase() || '')
    ).length;

    // --- All Popup Handlers and Flow Logic (Existing - no changes needed here) ---
     const handleTriggerAssignmentFlow = useCallback((orderItemIdOrBookingData) => {
        let itemId = null;
        if (typeof orderItemIdOrBookingData === 'number') { itemId = orderItemIdOrBookingData; }
        else if (orderItemIdOrBookingData?.order_item_id) { itemId = orderItemIdOrBookingData.order_item_id; }
        else if (orderItemIdOrBookingData?.id) { itemId = orderItemIdOrBookingData.id; }

        if (itemId) {
            console.log(`[DashboardWithChat] Triggering assignment flow for Order Item ID: ${itemId}`);
            setIsAssignPopupOpen(true);
            setOrderItemIdForAssignment(itemId);
            setIsUploadPopupOpen(false);
            setSelectedOrderItemIdForUpload(null);
            setIsFieldsPopupOpen(false);
            setOrderItemIdForFields(null);
            setFieldsDefinitionForPopup(null);
            setIsLoadingFieldsDef(false);
            setFieldsContextData({ serviceName: null, applicantName: null, applicantId: null });
        } else {
            console.error("[DashboardWithChat] Cannot start assignment flow: Invalid data provided.", orderItemIdOrBookingData);
            toast.error("Could not start document process: missing required information.");
        }
    }, []);

    const handleCloseAssignPopup = useCallback(async (assignmentSucceeded) => {
        console.log(`[DashboardWithChat] Closing Assign Applicant popup. Assignment success: ${assignmentSucceeded}`);
        const previouslyTargetedItemId = orderItemIdForAssignment;
        setIsAssignPopupOpen(false);
        setOrderItemIdForAssignment(null);
        setFieldsDefinitionForPopup(null);
        setFieldsContextData({ serviceName: null, applicantName: null, applicantId: null });
        setIsLoadingFieldsDef(false);

        if (assignmentSucceeded && previouslyTargetedItemId) {
            console.log(`[DashboardWithChat] Assignment successful for Item ID: ${previouslyTargetedItemId}. Fetching process details...`);
            setIsLoadingFieldsDef(true);
            try {
                const response = await apiClient.get(`/order-items/${previouslyTargetedItemId}/process-details`);
                const details = response.data;
                console.log(`[DashboardWithChat] Process details fetched for ${previouslyTargetedItemId}:`, details);
                const definitionFromApi = details?.additional_fields_definition;
                let parsedDef = null;
                const apiType = typeof definitionFromApi;
                const isApiArray = Array.isArray(definitionFromApi);
                console.log(`[DashboardWithChat] Processing definition. Type: ${apiType}, Is Array: ${isApiArray}, Value:`, definitionFromApi);

                if (isApiArray && definitionFromApi.length > 0) {
                    console.log(`[DashboardWithChat] Definition is a non-empty array. Using directly.`);
                    parsedDef = definitionFromApi;
                } else if (apiType === 'string' && definitionFromApi.trim().startsWith('[')) {
                    console.log(`[DashboardWithChat] Definition is a string. Attempting to parse...`);
                    try {
                        parsedDef = JSON.parse(definitionFromApi);
                        if (!Array.isArray(parsedDef) || parsedDef.length === 0) {
                            console.warn(`[DashboardWithChat] Parsed string resulted in empty or non-array value. Discarding.`);
                            parsedDef = null;
                        } else {
                             console.log(`[DashboardWithChat] Successfully parsed definition string into ${parsedDef.length} field(s).`);
                        }
                    } catch (e) {
                        console.error(`Error parsing additional_fields_definition JSON string:`, e);
                        toast.error("Failed to parse field requirements string.");
                        parsedDef = null;
                    }
                } else {
                    console.log(`[DashboardWithChat] No valid/non-empty definition found (received type: ${apiType}, isArray: ${isApiArray}, length: ${isApiArray ? definitionFromApi.length : 'N/A'}). Setting to null.`);
                    parsedDef = null;
                }
                console.log(`[DashboardWithChat] Final parsedDef to be stored:`, parsedDef);
                setFieldsDefinitionForPopup(parsedDef);
                setFieldsContextData({
                    serviceName: details?.serviceName || 'N/A',
                    applicantName: details?.applicantName || 'N/A',
                    applicantId: details?.applicantId || null,
                });
                console.log(`[DashboardWithChat] Details processed. Opening Upload popup for ${previouslyTargetedItemId}.`);
                setSelectedOrderItemIdForUpload(previouslyTargetedItemId);
                setIsUploadPopupOpen(true);
                setRefreshHistoryTrigger(prev => prev + 1); // Refresh history after assignment
            } catch (err) {
                console.error(`Failed to fetch processing details for ${previouslyTargetedItemId}:`, err);
                const errorMsg = err.response?.data?.error || "Failed to load application requirements. Cannot proceed.";
                toast.error(errorMsg);
                setFieldsDefinitionForPopup(null);
                setFieldsContextData({ serviceName: null, applicantName: null, applicantId: null });
            } finally {
                setIsLoadingFieldsDef(false);
            }
        } else {
             console.log(`[DashboardWithChat] Assignment was not successful or cancelled.`);
             setFieldsDefinitionForPopup(null);
             setFieldsContextData({ serviceName: null, applicantName: null, applicantId: null });
             setIsLoadingFieldsDef(false);
        }
    }, [orderItemIdForAssignment]); // Keep dependency minimal

    const handleCloseUploadPopup = useCallback((didUploadSucceed) => {
        if (didUploadSucceed === null) {
            console.log("[DashboardWithChat] Operation in progress, keeping upload popup open."); return;
        }
        const previouslyUploadedItemId = selectedOrderItemIdForUpload;
        console.log(`[DashboardWithChat] Closing upload popup. Success: ${didUploadSucceed} for Item ID: ${previouslyUploadedItemId}`);
        setIsUploadPopupOpen(false);
        setSelectedOrderItemIdForUpload(null);

        if (didUploadSucceed === true && previouslyUploadedItemId) {
            toast.info("Documents handled. Checking for additional details...");
            setRefreshHistoryTrigger(prev => prev + 1); // Refresh history after upload changes status
            const isArray = Array.isArray(fieldsDefinitionForPopup);
            const length = isArray ? fieldsDefinitionForPopup.length : 0;
            const shouldOpenFields = isArray && length > 0;
             console.log(`[handleCloseUploadPopup] CHECKING STATE: fieldsDefinitionForPopup =`, fieldsDefinitionForPopup, `isArray = ${isArray}, length = ${length}, shouldOpen = ${shouldOpenFields}`);

            if (shouldOpenFields) {
                console.log(`[DashboardWithChat] Additional fields required (${length}). Opening fields popup.`);
                setOrderItemIdForFields(previouslyUploadedItemId);
                setIsFieldsPopupOpen(true);
            } else {
                console.log(`[DashboardWithChat] No additional fields required or definition missing/empty. Process step complete.`);
                setOrderItemIdForFields(null);
                setFieldsDefinitionForPopup(null); // Reset definition state as it's no longer needed for this flow
                setFieldsContextData({ serviceName: null, applicantName: null, applicantId: null }); // Reset context
                toast.success("Application details updated successfully!"); // Potentially adjust message
            }
        } else {
            console.log(`[DashboardWithChat] Upload popup closed without success or item ID missing.`);
            setOrderItemIdForFields(null);
            setFieldsDefinitionForPopup(null);
            setFieldsContextData({ serviceName: null, applicantName: null, applicantId: null });
            if (didUploadSucceed === false) { toast.warn("Document submission was cancelled or failed."); }
        }
    }, [selectedOrderItemIdForUpload, fieldsDefinitionForPopup]);

    const handleCloseFieldsPopup = useCallback((didSubmitSuccessfully) => {
        const processedItemId = orderItemIdForFields;
        console.log(`[DashboardWithChat] Closing additional fields popup for Item ID: ${processedItemId}. Success: ${didSubmitSuccessfully}`);
        setIsFieldsPopupOpen(false);
        setOrderItemIdForFields(null);
        setFieldsDefinitionForPopup(null);
        setFieldsContextData({ serviceName: null, applicantName: null, applicantId: null });

        if (didSubmitSuccessfully) {
            toast.success(`Application details updated successfully for Item ${processedItemId}!`);
            setRefreshHistoryTrigger(prev => prev + 1); // Refresh history after fields submission
        } else {
            toast.warn(`Additional details submission was cancelled or failed.`);
        }
    }, [orderItemIdForFields]);
    // --- End Popup Handlers ---


    // --- Tab Activation Effect (Existing) ---
    useEffect(() => {
        if (activeTab === 'history') {
            console.log('[DashboardWithChat] History tab activated, potentially refreshing data (now handled by main effect)...');
        }
    }, [activeTab]);

    // --- Styles Definition (Existing) ---
    const applicationHistoryStyles = {
        section: { marginBottom: '2rem' },
        sectionTitle: { fontSize: '1.6rem', fontWeight: 600, color: '#34495e', display: 'flex', alignItems: 'center', gap: '0.7rem', marginBottom: '1.5rem', borderBottom: '2px solid #e0e0e0', paddingBottom: '0.6rem' },
        cardsContainer: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginTop: '1rem', },
        card: { backgroundColor: '#ffffff', borderRadius: '10px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', overflow: 'hidden', display: 'flex', flexDirection: 'column', transition: 'box-shadow 0.3s ease', border: '1px solid #eee', '&:hover': { boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12)', } },
        cardBody: { padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.9rem', flexGrow: 1 },
        cardField: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', },
        cardLabel: { fontWeight: 500, color: '#6c757d', fontSize: '0.85rem', marginRight: '0.5rem', },
        cardValue: { fontSize: '0.95rem', color: '#343a40', textAlign: 'right', },
        statusBadge: (status) => { const baseStyle = { padding: '0.3rem 0.8rem', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 500, textAlign: 'center', display: 'inline-block', border: '1px solid transparent', textTransform: 'capitalize'}; switch (status?.toLowerCase()) { case 'completed': case 'approved': return { ...baseStyle, backgroundColor: '#dcfce7', color: '#166534', borderColor: '#86efac' }; case 'processing': case 'documents submitted': return { ...baseStyle, backgroundColor: '#dbeafe', color: '#1e40af', borderColor: '#93c5fd' }; case 'pending information': case 'pending applicant info': case 'pending documents': return { ...baseStyle, backgroundColor: '#fef3c7', color: '#92400e', borderColor: '#fcd34d' }; case 'pending': return { ...baseStyle, backgroundColor: '#f3e8ff', color: '#581c87', borderColor: '#c084fc' }; case 'rejected': return { ...baseStyle, backgroundColor: '#fee2e2', color: '#991b1b', borderColor: '#fca5a5' }; case 'cart': return { ...baseStyle, backgroundColor: '#e5e7eb', color: '#4b5563', borderColor: '#d1d5db' }; default: return { ...baseStyle, backgroundColor: '#f3f4f6', color: '#4b5563', borderColor: '#e5e7eb' }; } },
        cardActions: { borderTop: '1px solid #f1f1f1', padding: '0.75rem 1.25rem', backgroundColor: '#f8f9fa', display: 'flex', justifyContent: 'flex-end', },
        cardActionButton: { backgroundColor: '#007bff', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.9rem', fontWeight: 500, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', transition: 'background-color 0.2s ease, box-shadow 0.2s ease', boxShadow: '0 2px 4px rgba(0, 123, 255, 0.2)', },
        cardActionButtonHover: { backgroundColor: '#0056b3', boxShadow: '0 4px 8px rgba(0, 123, 255, 0.3)', },
        noDataMessage: { gridColumn: '1 / -1', textAlign: 'center', padding: '3rem 1rem', color: '#6c757d', fontStyle: 'italic', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px dashed #dee2e6', },
        loadingIndicator: { padding: '3rem 1rem', textAlign: 'center', color: '#6c757d', fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gridColumn: '1 / -1', },
        errorIndicator: { padding: '1.5rem', margin: '1rem 0', textAlign: 'center', color: '#721c24', border: '1px solid #f5c6cb', borderRadius: '6px', backgroundColor: '#f8d7da', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', gridColumn: '1 / -1', },
    };

    // Get chat context value
    const chatContextValue = useContext(ChatContext);

    return (
        <ChatProvider 
            triggerDocumentUploadImplementation={handleTriggerAssignmentFlow}
            triggerDataRefresh={triggerDataRefresh} // Add this prop
        >
            {/* Render the main Dashboard UI Component, passing down counts AND the new callback */}
            <Dashboard
                 // --- Existing props ---
                 bookingHistory={bookingHistory}
                 isLoadingHistory={isLoadingHistory}
                 errorHistory={errorHistory}
                 historyStyles={applicationHistoryStyles}
                 activeTab={activeTab}
                 setActiveTab={setActiveTab}
                 isLoadingFieldsDef={isLoadingFieldsDef}
                 handleTriggerAssignmentFlow={handleTriggerAssignmentFlow}
                 chatContextValue={chatContextValue}
                 cartItemCount={cartItemCount}
                 historyActionCount={historyActionCount}
                 // --- *** NEW: Pass the refresh trigger function *** ---
                 triggerDataRefresh={triggerDataRefresh}
            />

            {/* --- Render Popups Here (Existing) --- */}
            <AssignApplicantPopup
                isOpen={isAssignPopupOpen}
                onClose={handleCloseAssignPopup}
                orderItemId={orderItemIdForAssignment}
             />
            {isUploadPopupOpen && selectedOrderItemIdForUpload && (
                <UploadDocumentsPopup
                    isOpen={isUploadPopupOpen}
                    onClose={handleCloseUploadPopup}
                    orderItemId={selectedOrderItemIdForUpload}
                />
             )}
            {isFieldsPopupOpen && orderItemIdForFields && fieldsDefinitionForPopup && (
                 <AdditionalFieldsPopup
                     isOpen={isFieldsPopupOpen}
                     onClose={handleCloseFieldsPopup}
                     orderItemId={orderItemIdForFields}
                     fieldsDefinition={fieldsDefinitionForPopup}
                     serviceName={fieldsContextData.serviceName}
                     applicantName={fieldsContextData.applicantName}
                     applicantId={fieldsContextData.applicantId}
                 />
             )}
        </ChatProvider>
    );
};

export default DashboardWithChat;